/**
 * Safe accessors for browser globals (navigator, location, document, performance).
 *
 * Bots, anti-fraud/anti-scraping tools, privacy extensions and headless
 * automation routinely REPLACE these objects - or, more often, redefine their
 * individual property getters - with their own injected JavaScript. That code
 * is frequently buggy, so merely *reading* a property such as
 * `window.navigator.userAgent` runs the stubbed getter and can throw. We have
 * seen `RangeError: Maximum call stack size exceeded` and
 * `ReferenceError: opts is not defined` surface from `Navigator.get` in the
 * wild on real client pages.
 *
 * As with Web Storage (see `safeStorage` in ./session), a `typeof` probe is NOT
 * a safe guard here because it still invokes the throwing getter. Every read is
 * therefore attempted inside try/catch and degrades to a safe default, so:
 *   1. a stubbed/broken global can never throw onto the host page, and
 *   2. the rest of the session profile (UTMs, click IDs, channel grouping,
 *      lead sources) is still produced instead of being lost to one bad getter.
 *
 * This is the resilience charter's invariant #2 - "attempt-and-catch, never
 * probe" - applied to navigator/location/document. See REFERENCE.md §11.
 *
 * How this module is built
 * ------------------------
 * Two kinds of function, deliberately kept apart (invariant #10):
 *
 *   - `coerce*` are PURE policy. They decide what an untrustworthy value means -
 *     `0` is a real zero, `NaN` is not a number, absence is `null` and never
 *     `false`. No I/O, no try/catch, testable with literals.
 *   - `safe*` are the GUARDED accessors. Each one owns its own try/catch, sited
 *     at the read it protects, and hands the result to a `coerce*`.
 *
 * These were previously one function taking a callback (`tryReadString(() => …)`),
 * which read better as DRY but put the try/catch a module away from the danger and
 * hid whether a read was actually deferred into the guard. It hid it in production
 * too: `collectResourceTiming` passed an already-evaluated value to that callback
 * for months, so its guard caught nothing. The type system cannot catch that -
 * `any` is assignable to `() => any`, and every hostile-getter read is `as any` by
 * necessity, because the declared type of a stubbed object is not trustworthy.
 * Guards are now visible at the point of danger instead.
 */

type NavigatorStringProperty = 'userAgent' | 'vendor' | 'language';

/**
 * Coerce an untrusted value to a string, mapping absence to ''.
 *
 * Pure. A non-string result (some stubs hand back numbers or objects) is coerced
 * with String() so callers always receive a string.
 */
export function coerceString(value: unknown) : string {
    return value ? String(value) : '';
}

/**
 * Coerce an untrusted value to a boolean, mapping absence to null.
 *
 * `coerceString` cannot express these signals: it collapses `false` to '' and so
 * makes "the browser reports no network" indistinguishable from "we could not find
 * out". That distinction decides whether a fetch failure is classified as the
 * user's connectivity or the config host's, so absence must stay separable from
 * false. null means unknown - never treat it as false.
 */
export function coerceBoolean(value: unknown) : boolean | null {
    return value === undefined || value === null ? null : !!value;
}

/**
 * Coerce an untrusted value to a finite number, mapping absence and non-numbers to
 * null. As with `coerceBoolean`, null means unknown: a real `0` (e.g. an rtt of
 * zero) must survive as 0, not be flattened away.
 */
export function coerceNumber(value: unknown) : number | null {
    if (value === undefined || value === null || value === '') {
        return null;
    }
    const numeric = Number(value);
    // isFinite rejects NaN and +/-Infinity; some stubs hand back both
    return isFinite(numeric) ? numeric : null;
}

/**
 * Guarded read of one property off an untrustworthy object, preserving the difference
 * between a MEASURED value and an unreadable one.
 *
 * null means "we could not read it". That distinction has to survive for any field a
 * rule treats as evidence: collapsing an unreadable `transferSize` to 0 let one hostile
 * getter fabricate a confident "the response carried no bytes" verdict. Where a caller
 * genuinely only needs a display value, `safeNumberOf` supplies the 0 default.
 */
export function safeNumberOrNull(source: any, property: string) : number | null {
    try {
        return coerceNumber(source ? source[property] : null);
    } catch (ignored) {
        return null;
    }
}

/**
 * Read a string-valued `navigator` property, returning '' if anything throws or
 * the value is absent. Both the `window.navigator` access and the individual
 * property access happen inside the guard: each property is its own getter and a
 * bot may stub one (e.g. `userAgent`) while leaving others intact.
 */
export function safeNavigatorString(property: NavigatorStringProperty) : string {
    try {
        // Reading `window.navigator` itself can throw when the whole object has
        // been swapped for a throwing/recursive getter, so it lives in the guard.
        const nav = window.navigator;
        // Cast through `any`: the value comes from a possibly-stubbed object, so
        // the declared type is not trustworthy.
        return coerceString(nav ? (nav as any)[property] : '');
    } catch (ignored) {
        return '';
    }
}

/**
 * Read `window.location.hostname` safely, returning '' if it throws or is unset.
 */
export function safeLocationHostname() : string {
    return safeLocationString('hostname');
}

/**
 * Read `window.location.search` (the query string, incl. leading '?') safely,
 * returning '' if it throws or is unset.
 */
export function safeLocationSearch() : string {
    return safeLocationString('search');
}

/**
 * Guarded read of one string-valued `window.location` property.
 *
 * Parameterised by property, like `safeNavigatorString`, so the two accessors above
 * share one guard instead of repeating it. That matters here beyond tidiness: jsdom
 * defines `window.location` non-configurably at every level, so this catch is the
 * one branch in this module that CANNOT be exercised by a test (see the note in
 * tests/unit/safeglobals.spec.ts). Keeping it to a single site keeps the untestable
 * surface as small as possible.
 */
function safeLocationString(property: 'hostname' | 'search') : string {
    try {
        const location = window.location;
        return coerceString(location ? location[property] : '');
    } catch (ignored) {
        return '';
    }
}

/**
 * Read `document.referrer` safely, returning '' if it throws or is unset.
 */
export function safeDocumentReferrer() : string {
    try {
        return coerceString(document ? document.referrer : '');
    } catch (ignored) {
        return '';
    }
}

/**
 * Parse the hostname out of an arbitrary URL string (e.g. a referrer),
 * returning '' if it is not a valid absolute URL. Unlike the accessors above,
 * the throwing input here isn't a stubbed getter but the `URL` constructor
 * itself: it's absent on IE11 and throws on a malformed value, so the same
 * guarded-read pattern applies.
 */
export function safeUrlHostname(url: string) : string {
    try {
        return coerceString(new URL(url).hostname);
    } catch (ignored) {
        return '';
    }
}

/**
 * Read `navigator.onLine`, returning null when the read throws or the property
 * is absent.
 *
 * Interpretation matters: `false` is a reasonably trustworthy "no network
 * interface", but `true` proves nothing - it only means an interface exists, not
 * that anything is reachable (captive portals report true). So only the false
 * case should be used as evidence.
 */
export function safeNavigatorOnLine() : boolean | null {
    try {
        const nav = window.navigator;
        return coerceBoolean(nav ? (nav as any).onLine : null);
    } catch (ignored) {
        return null;
    }
}

/**
 * Read `document.visibilityState`, returning '' if it throws or is unset.
 *
 * Captured because browsers throttle timers and requests in backgrounded tabs,
 * which is the dominant false-positive source for request timeouts: a 'hidden'
 * tab that timed out usually says nothing about the server.
 */
export function safeVisibilityState() : string {
    try {
        return coerceString(document ? (document as any).visibilityState : '');
    } catch (ignored) {
        return '';
    }
}

/**
 * Starting points captured from BOTH clocks available to a page.
 *
 * Plain data, not a closure over the two numbers, so it can be logged, asserted on
 * and serialized - and so `elapsedMs` stays a pure function of it (invariant #10).
 * `null` means that clock was unreadable when the measurement started.
 */
export interface TimerBases {
    monotonicStart: number | null;
    wallStart: number | null;
}

/**
 * Begin measuring elapsed time, robust to either clock breaking mid-measurement.
 *
 * Captures a starting point on both clocks because each may independently be
 * absent or stubbed by page scripts. Pair with `elapsedMs`.
 *
 * IMPORTANT: never compute a duration by differencing two independent "now" reads.
 * If `performance` breaks between them the two values come from clocks with
 * different epochs (~page load vs 1970) and the difference is garbage (~55,000
 * years). `elapsedMs` exists to make that mistake unavailable.
 */
export function startTimer() : TimerBases {
    return {
        monotonicStart: safeMonotonicNow(),
        wallStart: safeWallNow(),
    };
}

/**
 * Milliseconds elapsed since `bases` was captured, or -1 when unknowable.
 *
 * Pure with respect to `bases`: it differences each reading only against the base
 * from the SAME clock, preferring the monotonic pair (immune to a wall-clock change
 * mid-request), falling back to the wall pair, and returning -1 when neither pair
 * is complete.
 *
 * -1 must be treated as "unknown". In particular it must never satisfy an
 * `elapsed < threshold` check that means "failed instantly" - a missing measurement
 * is not a fast one, and reading it as one fabricates a "refused locally" verdict.
 */
export function elapsedMs(bases: TimerBases) : number {
    const monotonic = deltaSince(bases.monotonicStart, safeMonotonicNow);
    if (monotonic !== null) {
        return monotonic;
    }
    const wall = deltaSince(bases.wallStart, safeWallNow);
    if (wall !== null) {
        return wall;
    }
    return -1;
}

/**
 * Elapsed time on one clock, or null when that clock cannot answer.
 *
 * A NEGATIVE delta is null, not 0. Time did not run backwards - the clock was
 * adjusted (NTP correction, a user changing their system time, a stubbed
 * `performance.now`), so the measurement is void. Clamping it to 0 would have
 * published an impossible measurement as a real one, and 0 is the value the failure
 * classifier reads as "failed too fast to have made a network round trip". A void
 * measurement must be unknown, so the caller falls through to the other clock and
 * ultimately to -1.
 */
function deltaSince(base: number | null, read: () => number | null) : number | null {
    if (base === null) {
        return null;
    }
    const end = read();
    if (end === null) {
        return null;
    }
    const delta = end - base;
    return delta >= 0 ? delta : null;
}

/** Guarded `performance.now()`. null when absent or stubbed. Monotonic. */
function safeMonotonicNow() : number | null {
    try {
        return coerceNumber(window.performance.now());
    } catch (ignored) {
        return null;
    }
}

/** Guarded `Date.now()`. null when stubbed. Subject to wall-clock changes. */
function safeWallNow() : number | null {
    try {
        return coerceNumber(Date.now());
    } catch (ignored) {
        return null;
    }
}

/**
 * Network Information API summary. Every field is optional and experimental, and
 * the whole API is absent on Safari and older browsers.
 */
export interface ConnectionInfo {
    effectiveType: string;
    rtt: number | null;
    downlink: number | null;
    saveData: boolean | null;
}

/**
 * Read `navigator.connection` (with the vendor-prefixed aliases still present on
 * older Android builds), returning an all-empty summary when absent.
 *
 * Each field is guarded separately rather than with one try/catch around the whole
 * object: a privacy extension may stub `effectiveType` while leaving `rtt` intact,
 * and per-object guarding would discard the fields that still worked. Invariant #2
 * also rules out a `typeof` probe here - checking the property runs the very getter
 * that may throw.
 */
export function safeConnectionInfo() : ConnectionInfo {
    return {
        effectiveType: coerceString(readConnectionProperty('effectiveType')),
        rtt: coerceNumber(readConnectionProperty('rtt')),
        downlink: coerceNumber(readConnectionProperty('downlink')),
        saveData: coerceBoolean(readConnectionProperty('saveData')),
    };
}

/**
 * Guarded read of one `navigator.connection` property, returning null when the
 * API, the object or that individual getter is unavailable.
 *
 * The property arrives as a string because these four fields differ only by name;
 * the object it indexes is untyped anyway, since it comes from an experimental API
 * that page scripts routinely stub.
 */
function readConnectionProperty(property: string) : unknown {
    try {
        const nav = window.navigator as any;
        if (!nav) {
            return null;
        }
        const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
        return connection ? connection[property] : null;
    } catch (ignored) {
        return null;
    }
}
